import { MatchedTerm, SearchFilterField, SearchRouteKey } from '../models/search-filter-spec.model';
import { VocabularyEntry } from '../state/search-vocabulary.store';

export const MATCH_CONFIDENCE = {
  exact: 1,
  prefix: 0.8,
  substring: 0.6,
  keyword: 1,
  licenseNoNoDigit: 0.3,
  licenseNoWithDigit: 0.5,
} as const;

/** Fixed, locally-defined reserved-word tables — no server round trip, no NLP. */
export const ROUTE_KEYWORDS: Record<string, SearchRouteKey> = {
  report: 'reports',
  reports: 'reports',
  workspace: 'workspace',
};

export const LICENSE_TERM_KEYWORDS: Record<string, 'perpetual' | 'fixed'> = {
  perpetual: 'perpetual',
  'fixed-term': 'fixed',
  'fixed term': 'fixed',
};

/** Rank used ONLY to order an ambiguous token's candidates (rarer/more specific vocabularies
    first) — never to silently drop the others. Index = priority; lower index wins. */
const FIELD_PRIORITY: SearchFilterField[] = [
  'status', 'entities', 'licenseType', 'department', 'user', 'licenseTerm', 'startDate', 'endDate',
];

/** One field's best match for a token, carrying the internal confidence the parser (Task 5)
    uses to rank interpretations — NOT part of the public `MatchedTerm` shape. */
export interface TokenCandidate {
  term: MatchedTerm;
  matchConfidence: number;
}

/**
 * Checks a single already-normalized token against the reserved route/license-term keyword
 * tables. Returns null when the token is not reserved.
 */
export function matchReservedKeyword(
  token: string,
): { route?: SearchRouteKey; licenseTerm?: 'perpetual' | 'fixed' } | null {
  if (Object.hasOwn(ROUTE_KEYWORDS, token)) return { route: ROUTE_KEYWORDS[token] };
  if (Object.hasOwn(LICENSE_TERM_KEYWORDS, token)) return { licenseTerm: LICENSE_TERM_KEYWORDS[token] };
  return null;
}

/** Generic/connector words stripped before vocabulary matching. Left unfiltered, a token like
    "of" or "license" can exact/substring-match an unrelated multi-word master (e.g. a license
    type literally named "FSSAI license of kitchen" contains the word "license"), silently
    applying a spurious filter alongside a correct one from a different token. license/licence/
    licenses/licences are included because nearly every license-type/master name in this domain
    legitimately contains that word, making it meaningless as a discriminator. */
const STOP_WORDS = new Set([
  'list', 'show', 'all', 'of', 'for', 'the', 'and',
  'license', 'licence', 'licenses', 'licences',
  // The abbreviation stub left behind once a "license no"/"lic no" label prefix is stripped —
  // meaningless as a standalone search value (reported bug: "License no - MH-12/GC/0000/0000"
  // was parsed as licenseNo:'no', discarding the actual number).
  'no',
]);

export function isStopWord(token: string): boolean {
  return STOP_WORDS.has(token);
}

/** Below this length, a token may only EXACT-match a whole vocabulary word — prefix/substring
    matching on 1-2 character fragments produces near-random hits. */
const MIN_FUZZY_TOKEN_LENGTH = 3;

/**
 * Matches one already-normalized token against the vocabulary: exact match wins over prefix,
 * which wins over substring, PER FIELD — so an ambiguous token (matching more than one field)
 * still returns one candidate per field, each at that field's own best confidence. Comparisons
 * are against each entry's individual WORDS (not the raw whole `normalizedText`), so a token
 * like "kitchen" matching one word of a multi-word master name ("fssai license of kitchen")
 * scores as a whole-word EXACT hit, not an accidental substring hit against unrelated words.
 */
export function matchToken(token: string, vocabulary: VocabularyEntry[]): TokenCandidate[] {
  const byField = new Map<SearchFilterField, TokenCandidate>();

  for (const entry of vocabulary) {
    const words = entry.normalizedText.split(' ');
    let confidence: number | null = null;
    if (words.includes(token)) {
      confidence = MATCH_CONFIDENCE.exact;
    } else if (token.length >= MIN_FUZZY_TOKEN_LENGTH) {
      if (words.some((w) => w.startsWith(token))) confidence = MATCH_CONFIDENCE.prefix;
      else if (words.some((w) => w.includes(token))) confidence = MATCH_CONFIDENCE.substring;
    }
    if (confidence === null) continue;

    const existing = byField.get(entry.field);
    if (existing && existing.matchConfidence >= confidence) continue;

    byField.set(entry.field, {
      matchConfidence: confidence,
      term: { token, field: entry.field, value: entry.value, label: entry.label },
    });
  }

  return [...byField.values()].sort(
    (a, b) => FIELD_PRIORITY.indexOf(a.term.field) - FIELD_PRIORITY.indexOf(b.term.field),
  );
}

/**
 * Matches an already-normalized VALUE (the remainder after a field-label prefix like
 * "department -", possibly multiple words) against a vocabulary already scoped by the caller to
 * one field (and role, for performer/reviewer). Unlike {@link matchToken}, this compares the
 * WHOLE value against each entry's WHOLE `normalizedText` — never word-by-word — so a multi-word
 * name ("Human Resources") scores as ONE exact hit instead of two independent per-word hits that
 * would otherwise get pushed twice into an array-valued filter. Exact wins over prefix, which
 * wins over substring; returns the single best-confidence candidate, or `null` when nothing
 * matches.
 */
export function matchFieldValue(value: string, vocabulary: VocabularyEntry[]): TokenCandidate | null {
  let best: TokenCandidate | null = null;

  for (const entry of vocabulary) {
    let confidence: number | null = null;
    if (entry.normalizedText === value) {
      confidence = MATCH_CONFIDENCE.exact;
    } else if (value.length >= MIN_FUZZY_TOKEN_LENGTH) {
      if (entry.normalizedText.startsWith(value)) confidence = MATCH_CONFIDENCE.prefix;
      else if (entry.normalizedText.includes(value)) confidence = MATCH_CONFIDENCE.substring;
    }
    if (confidence === null) continue;
    if (best && best.matchConfidence >= confidence) continue;

    best = { matchConfidence: confidence, term: { token: value, field: entry.field, value: entry.value, label: entry.label } };
  }

  return best;
}
